import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Tooltip from "@radix-ui/react-tooltip";
import { Check, ChevronDown, ChevronRight, Moon, Sun } from "lucide-react";
import { command } from "../core";
import { useSession } from "../react/providers/SessionProvider";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useCommand } from "../react/hooks/useCommand";
import { buildOrgTree, initialsOf, workingStatus } from "./workspace-utils";
import type { OrgNode } from "./workspace-utils";

const INDENT = 22; // px per level — matches .company-row CSS
const COL_CENTER = 11; // chevron-column center inside a level

function collectExpandable(nodes: OrgNode[], acc: Set<string>): Set<string> {
    for (const n of nodes) {
        if (n.children.length) {
            acc.add(n.id);
            collectExpandable(n.children, acc);
        }
    }
    return acc;
}

export function OrganizationSwitcher() {
    const session = useSession();
    const runtime = useRuntime();
    const dispatch = useCommand();

    const orgs = session.allowedOrganizations;
    const tree = useMemo(() => buildOrgTree(orgs), [orgs]);
    const labelById = useMemo(() => new Map(orgs.map((o) => [o.id, o.name])), [orgs]);

    // Committed scope: active org + the persisted visible set (defaults to [active]).
    const committedVisible = useMemo(() => {
        const stored = runtime.session.getVisibleOrgs(session.userId);
        return new Set(stored && stored.length ? stored : [session.activeOrganizationId]);
    }, [runtime, session.userId, session.activeOrganizationId]);

    const [open, setOpen] = useState(false);
    const [pendingActive, setPendingActive] = useState(session.activeOrganizationId);
    const [pendingVisible, setPendingVisible] = useState<Set<string>>(committedVisible);
    const [expanded, setExpanded] = useState<Set<string>>(() => collectExpandable(tree, new Set()));

    // Tooltip suppression for ~700ms after popover closes — outside-click
    // returns pointer near the trigger and Radix Tooltip would otherwise
    // re-open immediately. Only fires on true → false transitions.
    const wasOpen = useRef(false);
    const [tooltipSuppressed, setTooltipSuppressed] = useState(false);
    // Always-controlled tooltip — see AppSwitcher for the rationale. We
    // mirror Radix's hover state into `tipOpen` and force `false` while the
    // menu is open / suppressed, so Radix never receives `undefined` (which
    // would flip it between controlled and uncontrolled and warn).
    const [tipOpen, setTipOpen] = useState(false);
    useEffect(() => {
        if (open) {
            wasOpen.current = true;
            return;
        }
        if (!wasOpen.current) return;
        wasOpen.current = false;
        setTooltipSuppressed(true);
        const t = setTimeout(() => setTooltipSuppressed(false), 700);
        return () => clearTimeout(t);
    }, [open]);

    useEffect(() => {
        if (open) {
            setPendingActive(session.activeOrganizationId);
            setPendingVisible(new Set(committedVisible));
        }
    }, [open, session.activeOrganizationId, committedVisible]);

    const active = orgs.find((o) => o.id === session.activeOrganizationId) ?? orgs[0];
    const visibleCount = committedVisible.size;

    const isDirty =
        pendingActive !== session.activeOrganizationId ||
        pendingVisible.size !== committedVisible.size ||
        [...pendingVisible].some((id) => !committedVisible.has(id));

    const setActive = (id: string) => {
        if (id === pendingActive) return;
        setPendingVisible((prev) => {
            const single = prev.size === 1 && prev.has(pendingActive);
            if (single) return new Set([id]);
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
        setPendingActive(id);
    };

    const toggleVisible = (id: string) => {
        setPendingVisible((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                if (next.size === 1) return prev; // keep ≥ 1
                next.delete(id);
                if (pendingActive === id) {
                    const fallback = next.values().next().value;
                    if (fallback) setPendingActive(fallback);
                }
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleExpanded = (id: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const apply = () => {
        if (pendingVisible.size === 0) return;
        runtime.session.setVisibleOrgs(session.userId, [...pendingVisible]);
        setOpen(false);
        if (pendingActive !== session.activeOrganizationId) {
            void dispatch(command("SWITCH_ORG", { organizationId: pendingActive }));
        }
    };

    const commitImmediately = (id: string) => {
        runtime.session.setVisibleOrgs(session.userId, [id]);
        setOpen(false);
        if (id !== session.activeOrganizationId) {
            void dispatch(command("SWITCH_ORG", { organizationId: id }));
        }
    };

    return (
        <DropdownMenu.Root open={open} onOpenChange={setOpen}>
            <Tooltip.Provider delayDuration={350}>
                <Tooltip.Root open={open || tooltipSuppressed ? false : tipOpen} onOpenChange={setTipOpen}>
                    <Tooltip.Trigger asChild>
                        <DropdownMenu.Trigger asChild>
                            <button className="brand" aria-label={`Switch organization — ${active?.name ?? ""}`}>
                                <span className="brand__icon">{initialsOf(active?.name ?? "?")}</span>
                                <span className="brand__info">
                                    <span className="brand__title">
                                        <span className="brand__name">{active?.name ?? "—"}</span>
                                        {visibleCount > 1 ? <span className="brand__count">({visibleCount})</span> : null}
                                    </span>
                                    <span className="brand__sub">
                                        <span className="brand__sub-text">{active?.code ?? ""}</span>
                                    </span>
                                </span>
                                <ChevronDown size={14} className="brand__chev" />
                            </button>
                        </DropdownMenu.Trigger>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content className="rdx-tooltip" side="bottom" align="center" sideOffset={8} collisionPadding={12}>
                            Switch organization
                            <Tooltip.Arrow className="rdx-tooltip__arrow" width={11} height={5} />
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip.Root>
            </Tooltip.Provider>

            <DropdownMenu.Portal>
                <DropdownMenu.Content className="rdx-menu org-menu" align="start" sideOffset={8} alignOffset={12}>
                    <div className="org-menu__head">
                        <DropdownMenu.Label className="rdx-menu__label">
                            {isDirty ? (
                                <span className="org-menu__label-multi">
                                    Switch to:&nbsp;<strong>{labelById.get(pendingActive) ?? "—"}</strong>
                                    {pendingVisible.size > 1 ? (
                                        <span className="org-menu__count">+{pendingVisible.size - 1}</span>
                                    ) : null}
                                </span>
                            ) : (
                                "Switch organization"
                            )}
                        </DropdownMenu.Label>
                        {isDirty ? (
                            <button type="button" className="org-menu__commit" onClick={apply} title="Apply selection">
                                Apply
                            </button>
                        ) : null}
                    </div>

                    <CompanyTree
                        nodes={tree}
                        level={0}
                        pendingActive={pendingActive}
                        pendingVisible={pendingVisible}
                        expanded={expanded}
                        onSetActive={setActive}
                        onToggleVisible={toggleVisible}
                        onToggleExpanded={toggleExpanded}
                        onCommit={commitImmediately}
                    />

                    <DropdownMenu.Separator className="rdx-menu__sep" />
                    <DropdownMenu.Item className="rdx-menu__item">
                        <span className="rdx-menu__item-primary">Manage organizations →</span>
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}

interface CompanyTreeProps {
    nodes: OrgNode[];
    level: number;
    pendingActive: string;
    pendingVisible: Set<string>;
    expanded: Set<string>;
    onSetActive: (id: string) => void;
    onToggleVisible: (id: string) => void;
    onToggleExpanded: (id: string) => void;
    onCommit: (id: string) => void;
    ancestorsHasMore?: boolean[];
}

function CompanyTree({
    nodes,
    level,
    pendingActive,
    pendingVisible,
    expanded,
    onSetActive,
    onToggleVisible,
    onToggleExpanded,
    onCommit,
    ancestorsHasMore = [],
}: CompanyTreeProps) {
    // Reserve the chevron column only when it earns its space: any sibling here
    // is expandable, or we're nested (level > 0) where the guide lines depend on
    // it. A flat root list drops it entirely so avatars sit flush.
    const levelHasExpandable = nodes.some((n) => n.children.length > 0);
    const showChevronColumn = level > 0 || levelHasExpandable;
    return (
        <>
            {nodes.map((node, idx) => {
                const isLast = idx === nodes.length - 1;
                const isPrimary = node.id === pendingActive;
                const isVisible = pendingVisible.has(node.id);
                const hasChildren = node.children.length > 0;
                const isExpanded = hasChildren && expanded.has(node.id);
                const clock = workingStatus(node.timezone);
                const secondary = [node.code, node.country].filter(Boolean).join(" · ");
                return (
                    <Fragment key={node.id}>
                        <div className="company-row" style={{ paddingLeft: `${level * INDENT}px` }}>
                            {ancestorsHasMore.map((more, i) =>
                                more && i > 0 ? (
                                    <span
                                        key={i}
                                        className="company-row__line company-row__line--v"
                                        style={{ left: `${(i - 1) * INDENT + COL_CENTER}px` }}
                                    />
                                ) : null,
                            )}
                            {level > 0 ? (
                                <>
                                    <span
                                        className={`company-row__line company-row__line--l-v ${isLast ? "is-last" : ""}`}
                                        style={{ left: `${(level - 1) * INDENT + COL_CENTER}px` }}
                                    />
                                    <span
                                        className="company-row__line company-row__line--l-h"
                                        style={{ left: `${(level - 1) * INDENT + COL_CENTER}px` }}
                                    />
                                </>
                            ) : null}

                            {showChevronColumn ? (
                                hasChildren ? (
                                    <button
                                        type="button"
                                        className={`company-row__chev ${isExpanded ? "is-expanded" : ""}`}
                                        aria-label={isExpanded ? `Collapse ${node.name}` : `Expand ${node.name}`}
                                        onClick={() => onToggleExpanded(node.id)}
                                    >
                                        <ChevronRight size={12} />
                                    </button>
                                ) : (
                                    <span className="company-row__chev-spacer" />
                                )
                            ) : null}

                            <DropdownMenu.Item
                                className={[
                                    "rdx-menu__item company-row__item",
                                    isPrimary ? "is-active" : "",
                                    isVisible && !isPrimary ? "is-allowed" : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                onSelect={(e) => {
                                    e.preventDefault();
                                    onSetActive(node.id);
                                }}
                                onDoubleClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onCommit(node.id);
                                }}
                            >
                                <span className="avatar avatar--sq">{initialsOf(node.name)}</span>
                                <span className="rdx-menu__item-text">
                                    <span className="rdx-menu__item-primary">{node.name}</span>
                                    <span className="rdx-menu__item-secondary">{secondary || "—"}</span>
                                </span>
                            </DropdownMenu.Item>

                            {clock ? (
                                <span
                                    className={`company-row__clock ${clock.state === "working" ? "is-working" : "is-offhours"}`}
                                    title={`${clock.localTime} · ${clock.state === "working" ? "Working hours" : "Off hours"}`}
                                >
                                    {clock.state === "working" ? <Sun size={14} /> : <Moon size={14} />}
                                </span>
                            ) : null}

                            <button
                                type="button"
                                role="checkbox"
                                aria-checked={isVisible}
                                tabIndex={-1}
                                className={`org-checkbox ${isVisible ? "is-checked" : ""}`}
                                onClick={() => onToggleVisible(node.id)}
                                title={isVisible ? "Remove from visible organizations" : "Include in visible organizations"}
                            >
                                {isVisible ? <Check size={11} /> : null}
                            </button>
                        </div>

                        {hasChildren && isExpanded ? (
                            <CompanyTree
                                nodes={node.children}
                                level={level + 1}
                                pendingActive={pendingActive}
                                pendingVisible={pendingVisible}
                                expanded={expanded}
                                onSetActive={onSetActive}
                                onToggleVisible={onToggleVisible}
                                onToggleExpanded={onToggleExpanded}
                                onCommit={onCommit}
                                ancestorsHasMore={[...ancestorsHasMore, !isLast]}
                            />
                        ) : null}
                    </Fragment>
                );
            })}
        </>
    );
}
